import { Autocomplete, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import { isAnalyticsInternalDevice, setAnalyticsInternalDevice } from "../../asset/js/analytics";

const ranges = [
  { label: "היום", days: 1 },
  { label: "7 ימים", days: 7 },
  { label: "30 ימים", days: 30 },
  { label: "90 ימים", days: 90 },
];

const labels = {
  visitors: "מבקרים",
  sessions: "ביקורים",
  registrations: "הרשמות",
  tenders: "מכרזים שנוצרו",
  bids: "הצעות שהוגשו",
};

function localDate(date) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function Funnel({ title, steps = [] }) {
  const maximum = Math.max(1, ...steps.map((step) => step.count));
  return (
    <Paper sx={{ p: 2.2, borderRadius: 3, border: "1px solid #E2ECF4", boxShadow: "0 8px 24px rgba(22,58,86,.06)" }}>
      <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", mb: 1.6 }}>{title}</Typography>
      <Stack spacing={1.25}>
        {steps.map((step, index) => (
          <Box key={step.event}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.45 }}>
              <Typography sx={{ fontFamily: "Assistant", color: "#405C70", fontSize: 14 }}>{step.label}</Typography>
              <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", fontSize: 14 }}>
                {step.count} {index > 0 ? `(${step.conversion}%)` : ""}
              </Typography>
            </Stack>
            <Box sx={{ height: 9, borderRadius: 8, bgcolor: "#EDF3F8", overflow: "hidden" }}>
              <Box sx={{ width: `${Math.max(step.count ? 4 : 0, (step.count / maximum) * 100)}%`, height: "100%", borderRadius: 8, background: "linear-gradient(90deg,#1884D8,#39A7F4)" }} />
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function DailyTrend({ days = [] }) {
  const maximum = Math.max(1, ...days.map((day) => day.visitors));
  return (
    <Paper sx={{ p: 2.2, mb: 2.5, borderRadius: 3, border: "1px solid #E2ECF4", boxShadow: "0 8px 24px rgba(22,58,86,.05)" }}>
      <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", mb: 1.5 }}>מבקרים לפי יום</Typography>
      {days.length === 0 ? (
        <Typography sx={{ fontFamily: "Assistant", color: "#72889A" }}>עדיין אין נתונים בטווח שנבחר.</Typography>
      ) : (
        <Box sx={{ height: 190, display: "flex", alignItems: "flex-end", gap: 0.8, overflowX: "auto", pb: 0.5 }}>
          {days.map((day) => (
            <Box key={day.date} title={`${day.date}: ${day.visitors} מבקרים`} sx={{ minWidth: 27, flex: days.length <= 30 ? 1 : "0 0 27px", textAlign: "center" }}>
              <Typography sx={{ fontFamily: "Assistant", fontSize: 11, fontWeight: 800, color: "#47657A", mb: 0.4 }}>{day.visitors}</Typography>
              <Box sx={{ height: `${Math.max(day.visitors ? 8 : 2, (day.visitors / maximum) * 135)}px`, borderRadius: "7px 7px 3px 3px", background: "linear-gradient(180deg,#35A8F5,#1478C5)" }} />
              <Typography sx={{ fontFamily: "Assistant", fontSize: 9.5, color: "#7890A2", mt: 0.5 }}>{day.date.slice(5)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default function AnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [internalDevice, setInternalDevice] = useState(isAnalyticsInternalDevice());
  const [updatingInternal, setUpdatingInternal] = useState(false);
  const [internalMessage, setInternalMessage] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [exclusionRevision, setExclusionRevision] = useState(0);
  const adminHeaders = { Authorization: `Bearer ${Cookies.get("admintoken")}` };

  const loadInternalUsers = () => axios.get(
    `${process.env.React_APP_BACKEND_URL}/api/admin/analytics/internal-users`,
    { headers: adminHeaders }
  ).then((response) => setInternalUsers(response.data?.users || []));

  useEffect(() => {
    axios.get(`${process.env.React_APP_BACKEND_URL}/api/admin/allusers`, { headers: adminHeaders })
      .then((response) => setAllUsers(response.data?.Bids || []))
      .catch(() => setAllUsers([]));
    loadInternalUsers().catch(() => setInternalUsers([]));
  }, []);

  const updateInternalUser = (userId, internal) => {
    setUpdatingUser(true);
    setInternalMessage("");
    axios.post(`${process.env.React_APP_BACKEND_URL}/api/admin/analytics/internal-user`, {
      user_id: userId,
      internal,
    }, { headers: adminHeaders })
      .then(() => loadInternalUsers())
      .then(() => {
        setSelectedUser(null);
        setExclusionRevision((value) => value + 1);
        setInternalMessage(internal
          ? "המשתמש הוגדר כמשתמש בדיקות וכל הפעילות שלו הוחרגה."
          : "המשתמש הוסר מרשימת משתמשי הבדיקות. אירועים חדשים שלו ייכללו בדוחות.");
      })
      .catch(() => setInternalMessage("לא הצלחנו לעדכן את משתמש הבדיקות. נסה שוב."))
      .finally(() => setUpdatingUser(false));
  };

  const toggleInternalDevice = () => {
    const nextValue = !internalDevice;
    setUpdatingInternal(true);
    setInternalMessage("");
    setAnalyticsInternalDevice(nextValue)
      .then(() => {
        setInternalDevice(nextValue);
        setInternalMessage(nextValue
          ? "המכשיר הזה הוחרג. גם הפעילות הקודמת שלו הוסרה מהדוחות."
          : "ההחרגה בוטלה והפעילות מהמכשיר תחזור להיכלל בדוחות.");
      })
      .catch(() => setInternalMessage("לא הצלחנו לעדכן את ההחרגה. נסה שוב."))
      .finally(() => setUpdatingInternal(false));
  };

  const dates = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    return { from: localDate(from), to: localDate(to) };
  }, [days]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios.get(`${process.env.React_APP_BACKEND_URL}/api/admin/analytics/report`, {
      params: dates,
      headers: { Authorization: `Bearer ${Cookies.get("admintoken")}` },
    }).then((response) => {
      if (active) setReport(response.data);
    }).catch(() => {
      if (active) setError("לא ניתן לטעון כרגע את נתוני האנליטיקה.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [dates, exclusionRevision]);

  return (
    <Box dir="rtl" sx={{ width: "100%", maxWidth: 1280, mx: "auto", px: { xs: 1.5, md: 3 }, py: 2, fontFamily: "Assistant" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={1.5} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#153B58" }}>אנליטיקת האתר</Typography>
          <Typography sx={{ fontFamily: "Assistant", color: "#668094" }}>נתוני שימוש פנימיים ללא זיהוי באמצעות טביעת דפדפן</Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ direction: "rtl", flexWrap: "wrap", gap: 1 }}>
          {ranges.map((range) => (
            <Button key={range.days} onClick={() => setDays(range.days)} variant={days === range.days ? "contained" : "outlined"} sx={{ fontFamily: "Assistant", fontWeight: 800, borderRadius: 2.5, textTransform: "none" }}>
              {range.label}
            </Button>
          ))}
          <Button onClick={toggleInternalDevice} disabled={updatingInternal} variant={internalDevice ? "contained" : "outlined"} color={internalDevice ? "success" : "warning"} sx={{ fontFamily: "Assistant", fontWeight: 900, borderRadius: 2.5, textTransform: "none" }}>
            {updatingInternal ? "מעדכן..." : internalDevice ? "המכשיר הזה מוחרג" : "החרג את המכשיר הזה"}
          </Button>
        </Stack>
      </Stack>
      {internalMessage && (
        <Paper sx={{ p: 1.4, mb: 2, borderRadius: 2.5, bgcolor: internalDevice ? "#EEF9F2" : "#FFF7E8", color: "#35566C", fontFamily: "Assistant", fontWeight: 700 }}>
          {internalMessage}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2.5, borderRadius: 3, border: "1px solid #E2ECF4" }}>
        <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", mb: 0.5 }}>
          החרגת חשבון משתמש לבדיקות
        </Typography>
        <Typography sx={{ fontFamily: "Assistant", color: "#668094", mb: 1.5 }}>
          פעילות החשבון תוחרג מכל דפדפן ומכל אפליקציה לאחר התחברות.
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ xs: "stretch", md: "center" }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={allUsers.filter((user) => !internalUsers.some((internalUser) => String(internalUser.id) === String(user.id)))}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            getOptionLabel={(user) => `${user.name || ""} ${user.last_name || ""} (${user.email || user.username || user.id})`.trim()}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            renderInput={(params) => <TextField {...params} label="בחר משתמש בדיקות" size="small" />}
          />
          <Button disabled={!selectedUser || updatingUser} onClick={() => updateInternalUser(selectedUser.id, true)} variant="contained" sx={{ fontFamily: "Assistant", fontWeight: 900, borderRadius: 2.5, textTransform: "none", minHeight: 40 }}>
            החרג משתמש
          </Button>
        </Stack>
        {internalUsers.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.8 }}>
            {internalUsers.map((user) => (
              <Chip
                key={user.id}
                label={`${user.name || ""} ${user.last_name || ""} (${user.email || user.username || user.id})`}
                onDelete={updatingUser ? undefined : () => updateInternalUser(user.id, false)}
                sx={{ fontFamily: "Assistant", fontWeight: 700 }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {loading && <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box>}
      {error && <Paper sx={{ p: 3, color: "#B42318", textAlign: "center", fontFamily: "Assistant" }}>{error}</Paper>}
      {!loading && report && (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5,1fr)" }, gap: 1.5, mb: 2.5 }}>
            {Object.entries(labels).map(([key, label]) => (
              <Paper key={key} sx={{ p: 2, borderRadius: 3, border: "1px solid #E2ECF4", textAlign: "center", boxShadow: "0 7px 20px rgba(22,58,86,.05)" }}>
                <Typography sx={{ fontFamily: "Assistant", color: "#698194", fontSize: 14 }}>{label}</Typography>
                <Typography sx={{ fontFamily: "Assistant", color: "#153B58", fontWeight: 900, fontSize: { xs: 27, md: 32 } }}>{report.overview?.[key] || 0}</Typography>
              </Paper>
            ))}
          </Box>

          <DailyTrend days={report.daily || []} />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2, mb: 2.5 }}>
            <Funnel title="משפך לקוחות" steps={report.funnels?.customer} />
            <Funnel title="משפך בעלי מקצוע" steps={report.funnels?.professional} />
            <Funnel title="משפך כניסה למכרז" steps={report.funnels?.guest_tender} />
            <Paper sx={{ p: 2.2, borderRadius: 3, border: "1px solid #E2ECF4" }}>
              <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", mb: 1.4 }}>מקורות תנועה</Typography>
              <Stack spacing={1}>
                {(report.sources || []).map((source) => (
                  <Stack key={source.source} direction="row" justifyContent="space-between" sx={{ borderBottom: "1px solid #EEF3F7", pb: 0.8 }}>
                    <Typography sx={{ fontFamily: "Assistant", color: "#405C70" }}>{source.source}</Typography>
                    <Typography sx={{ fontFamily: "Assistant", fontWeight: 900 }}>{source.visitors} מבקרים</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>

          <Paper sx={{ p: 2.2, borderRadius: 3, border: "1px solid #E2ECF4" }}>
            <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: "#173B57", mb: 1.4 }}>מכשירים</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {(report.devices || []).map((device) => (
                <Box key={device.device} sx={{ flex: 1, bgcolor: "#F5F9FC", p: 1.5, borderRadius: 2, textAlign: "center" }}>
                  <Typography sx={{ fontFamily: "Assistant", color: "#668094" }}>{device.device === "mobile" ? "נייד" : device.device === "tablet" ? "טאבלט" : device.device === "desktop" ? "מחשב" : "לא ידוע"}</Typography>
                  <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, fontSize: 24, color: "#173B57" }}>{device.visitors}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
}
